import React from "react";
import { Trash2, Plus, Loader2, X } from "lucide-react";
import api from "../../lib/api";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export default function AdminCalendrier() {
  const [cursor, setCursor] = React.useState(new Date());
  const [bookings, setBookings] = React.useState([]);
  const [blocked, setBlocked] = React.useState([]);
  const [showAdd, setShowAdd] = React.useState(false);
  const [addDate, setAddDate] = React.useState("");
  const [addReason, setAddReason] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  const firstISO = `${y}-${String(m + 1).padStart(2, "0")}-01`;
  const lastDay = new Date(y, m + 1, 0).getDate();
  const lastISO = `${y}-${String(m + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [b, bl] = await Promise.all([
        api.get(`/admin/bookings?from_date=${firstISO}&to_date=${lastISO}`),
        api.get(`/admin/blocked-slots`),
      ]);
      setBookings(b.data.filter(x => x.status !== "cancelled"));
      setBlocked(bl.data);
    } finally {
      setLoading(false);
    }
  }, [firstISO, lastISO]);

  React.useEffect(() => { load(); }, [load]);

  const addBlocked = async () => {
    if (!addDate) return;
    try {
      await api.post("/admin/blocked-slots", {
        date: addDate,
        start_time: "00:00",
        end_time: "23:59",
        reason: addReason || "Jour bloqué",
        all_day: true,
      });
      setShowAdd(false);
      setAddDate("");
      setAddReason("");
      load();
    } catch (e) {
      alert("Erreur : " + (e?.response?.data?.detail || "inconnue"));
    }
  };

  const removeBlocked = async (id) => {
    if (!confirm("Débloquer ce jour ?")) return;
    await api.delete(`/admin/blocked-slots/${id}`);
    load();
  };

  // Group by date
  const bookingsByDate = {};
  bookings.forEach(b => {
    bookingsByDate[b.date] = bookingsByDate[b.date] || [];
    bookingsByDate[b.date].push(b);
  });
  const blockedByDate = {};
  blocked.forEach(b => { blockedByDate[b.date] = b; });

  // Calendar grid
  const first = new Date(y, m, 1);
  const firstWeekday = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="text-text-cream" data-testid="admin-calendrier-page">
      <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">Admin</div>
          <h1 className="font-serif text-4xl">Calendrier</h1>
          <p className="text-text-cream/50 mt-2 text-sm font-light">Vue mensuelle · gérer blocages manuels (congés, prod perso).</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="block-day-btn">
          <Plus size={14} className="mr-2" /> Bloquer un jour
        </button>
      </div>

      <div className="bg-white/[0.02] border border-white/10 rounded p-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => setCursor(new Date(y, m - 1, 1))} className="text-text-cream/60 hover:text-accent-light">← Précédent</button>
          <div className="font-serif text-2xl">{MONTHS[m]} {y}</div>
          <button onClick={() => setCursor(new Date(y, m + 1, 1))} className="text-text-cream/60 hover:text-accent-light">Suivant →</button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {WEEKDAYS.map(w => (
            <div key={w} className="text-center text-text-cream/40 text-xs uppercase tracking-wider py-2">{w}</div>
          ))}
        </div>

        {loading && <div className="text-center p-8"><Loader2 className="animate-spin text-accent-deep mx-auto" /></div>}

        {!loading && (
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (!d) return <div key={i} className="aspect-square" />;
              const iso = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
              const dateObj = new Date(y, m, d);
              const isSunday = dateObj.getDay() === 0;
              const bks = bookingsByDate[iso] || [];
              const bl = blockedByDate[iso];
              return (
                <div
                  key={i}
                  data-testid={`day-${iso}`}
                  className={`aspect-square min-h-[90px] p-1.5 border rounded flex flex-col text-xs ${
                    isSunday ? "border-white/5 bg-white/[0.01] text-text-cream/30" :
                    bl ? "border-red-500/30 bg-red-500/10" :
                    bks.length ? "border-accent-deep/30 bg-accent-deep/5" :
                    "border-white/10"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-sm">{d}</span>
                    {bl && (
                      <button
                        data-testid={`unblock-${iso}`}
                        onClick={() => removeBlocked(bl.id)}
                        className="text-red-300/70 hover:text-red-300"
                      >
                        <X size={11} />
                      </button>
                    )}
                  </div>
                  {bl && (
                    <div className="mt-0.5 text-[0.6rem] text-red-300 uppercase tracking-wider">BLOQUÉ</div>
                  )}
                  {bks.slice(0, 3).map(b => (
                    <div key={b.id} className="mt-0.5 text-[0.65rem] text-accent-light truncate">
                      {b.start_time} · {b.client_prenom}
                    </div>
                  ))}
                  {bks.length > 3 && (
                    <div className="mt-0.5 text-[0.6rem] text-text-cream/50">+{bks.length - 3} autres</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 flex gap-4 text-xs text-text-cream/40">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-accent-deep/60 rounded" /> Réservation</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-red-500/60 rounded" /> Jour bloqué</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-white/10 rounded" /> Dimanche fermé</span>
        </div>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-dark border border-white/15 rounded p-8 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-serif text-2xl mb-6">Bloquer un jour</h3>
            <div className="space-y-4">
              <div>
                <label className="block eyebrow mb-2">Date</label>
                <input
                  data-testid="block-date-input"
                  type="date"
                  value={addDate}
                  onChange={e => setAddDate(e.target.value)}
                  className="w-full bg-white/5 border border-white/15 rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block eyebrow mb-2">Raison (optionnel)</label>
                <input
                  data-testid="block-reason-input"
                  type="text"
                  value={addReason}
                  onChange={e => setAddReason(e.target.value)}
                  placeholder="Vacances, formation, prod perso..."
                  className="w-full bg-white/5 border border-white/15 rounded px-3 py-2"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowAdd(false)} className="btn-ghost flex-1">Annuler</button>
              <button onClick={addBlocked} data-testid="confirm-block-btn" className="btn-primary flex-1">Bloquer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
